/**
 * ClearUndeposited — move client payments out of Undeposited Funds and onto the bank account.
 *
 * WHAT THIS IS FIXING. Client payments recorded as received and never deposited, sitting in
 * `1211 - Undeposited funds` instead of on the bank account.
 *
 * ⚠ The figure this was built from — $56,972.44 — was wrong and must not be repeated. Corrected:
 * **4 payments, $6,395, Aug-Dec 2025.** The old number came from filtering on a payment's
 * `assetAccount`, which records where the money originally landed and still reads `1211` long
 * after a deposit has cleared it, so it counted money already in the bank.
 *
 * IT IS NOT MISSING REVENUE, and the difference matters when explaining it: the income was
 * recorded, the invoices were right, the receivables were right, and the tax return is unaffected.
 * Two asset accounts, one holding money the other should. That is why this is a tidy-up rather
 * than an emergency — and also why it is worth doing properly rather than being left.
 *
 * A DEPOSIT IS A POINTER, NOT A NEW TRANSACTION. It references payments that already exist by id.
 * That is what makes double-counting structurally hard: clearing the same payment twice would mean
 * the same id on two deposits, which is visible.
 *
 * ONE BANK CREDIT USUALLY COVERS SEVERAL CHEQUES — a trip to the branch. So the matching is a
 * subset-sum, and unmatched credits are HELD rather than guessed at. See api/_financeDeposits.js.
 */

import { useState } from 'react'
import { planDeposits, commitDeposits, matchCoreDeposits } from '../../services/financeImport.js'
import { confirmDialog } from '../Dialog.jsx'

const money = (n) => '$' + Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function ClearUndeposited({ S, batch }) {
  const [plan, setPlan]     = useState(null)
  const [result, setResult] = useState(null)
  const [busy, setBusy]     = useState('')
  const [err, setErr]       = useState('')
  // Step one: what Core already has. The first run reports, the second writes the links.
  const [link, setLink]     = useState(null)

  async function doLink(apply) {
    setBusy(apply ? 'link-apply' : 'link'); setErr('')
    try {
      const r = await matchCoreDeposits(batch.id, apply)
      setLink(r)
      if (apply) setPlan(null)   // the scan below is now stale; it must be run again
    } catch (e) { setErr(e.message || String(e)) }
    finally { setBusy('') }
  }

  async function doPlan() {
    setBusy('plan'); setErr(''); setResult(null)
    try { setPlan(await planDeposits(batch.id)) }
    catch (e) { setErr(e.message || String(e)) }
    finally { setBusy('') }
  }

  async function doPost() {
    const will = (plan?.rows || []).filter(r => r.action === 'post')
    if (!will.length) return
    const ok = await confirmDialog({
      title: `Record ${will.length} deposit${will.length === 1 ? '' : 's'}?`,
      message: `${money(plan.totals.post)} moving out of Undeposited Funds and onto the bank `
        + 'account. Each one points at payments that already exist in Core, so nothing new is '
        + 'being invoiced or recognised — but Core has no reversal, so check the matches first.',
      confirmLabel: `Record ${will.length}`,
    })
    if (!ok) return

    setBusy('post'); setErr('')
    const created = []
    try {
      const ids = will.map(r => r.id)
      // Capped at 25 server-side, and it stops on the first refusal rather than pressing on.
      for (let i = 0; i < ids.length; i += 25) {
        const res = await commitDeposits(ids.slice(i, i + 25))
        created.push(...(res.created || []))
        if (res.failed?.length) {
          setResult({ created, failed: res.failed })
          setErr(`Stopped after ${created.length}. See what was refused below.`)
          setBusy(''); return
        }
      }
      setResult({ created, failed: [] })
      setPlan(await planDeposits(batch.id))
    } catch (e) {
      setErr(e.message || String(e)); setResult({ created, failed: [] })
    } finally { setBusy('') }
  }

  if (!batch) return null
  const will = (plan?.rows || []).filter(r => r.action === 'post')
  const held = (plan?.rows || []).filter(r => r.action === 'hold')

  return (
    <div style={S.card}>
      <div style={S.h}>Undeposited funds</div>

      <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
        Client payments recorded as received but never banked. The income and the invoices were
        always right — this only moves the money to the account that should be holding it.
      </div>

      {/* STEP ONE. Before asking which credits clear stranded payments, recognise the credits Core
          has already deposited and the ones that were never client money. Without this, every
          card payout in the year read as "unexplained" — 215 of 216 credits, on 2026-09-16. */}
      <div style={{ padding: '10px 12px', border: '0.5px solid var(--aj-border)', borderRadius: 6, marginBottom: 12, background: 'var(--aj-bg-subtle)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Step 1 · What Core already has</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={S.btn} disabled={!!busy} onClick={() => doLink(false)}>
            {busy === 'link' ? 'Checking…' : 'Check bank credits against what Core already has'}
          </button>
          {link && !link.applied && (link.matched.n + (link.matchedPayments?.n || 0) + link.transfers.n + link.ownerContributions.n) > 0 && (
            <button style={{ ...S.btn, ...S.primary }} disabled={!!busy} onClick={() => doLink(true)}>
              {busy === 'link-apply' ? 'Marking…' : 'Mark these ' + (link.matched.n + (link.matchedPayments?.n || 0) + link.transfers.n + link.ownerContributions.n) + ' so the scan skips them'}
            </button>
          )}
          {link?.applied && <span style={{ fontSize: 12, color: '#2e7d32' }}>Marked. Now run step 2.</span>}
        </div>
        {link && (
          <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.7 }}>
            <div><span style={S.mono}>{link.matched.n}</span> credits already deposited in Core, <span style={S.mono}>{money(link.matched.total)}</span> — Core holds {link.coreDeposits.n} deposits on this account, {money(link.coreDeposits.total)}.</div>
            <div><span style={S.mono}>{link.matchedPayments?.n || 0}</span> credits recorded straight to checking in Core as payments, <span style={S.mono}>{money(link.matchedPayments?.total || 0)}</span> — no deposit needed.
              {link.directPayments?.unreadable > 0 && <span style={{ color: '#b4493c' }}> Core would not return {link.directPayments.unreadable} payment ledger(s); run this again before marking.</span>}
            </div>
            <div><span style={S.mono}>{link.transfers.n}</span> transfers in from savings, <span style={S.mono}>{money(link.transfers.total)}</span> — not client money.</div>
            <div>
              <span style={S.mono}>{link.ownerContributions.n}</span> owner contributions, <span style={S.mono}>{money(link.ownerContributions.total)}</span> — not client money.
              {link.ownerContributions.alreadyInCore?.n > 0 && <> {link.ownerContributions.alreadyInCore.n} already in Core as equity deposits ({money(link.ownerContributions.alreadyInCore.total)}).</>}
              {link.ownerContributions.toRecord?.n > 0 && <> {link.ownerContributions.toRecord.n} still to record ({money(link.ownerContributions.toRecord.total)}), 50/50 Matt / Emily.</>}
            </div>
            <div><span style={S.mono}>{link.processorPayouts.n}</span> processor payouts with no deposit to the cent, <span style={S.mono}>{money(link.processorPayouts.total)}</span> — fees netted out; needs the payout report.</div>
            <div style={{ color: link.unexplained.n ? '#b4493c' : 'var(--aj-text-muted)' }}>
              <span style={S.mono}>{link.unexplained.n}</span> credits still unexplained, <span style={S.mono}>{money(link.unexplained.total)}</span>. These are the ones to look at.
            </div>
            {link.unexplained.rows?.length > 0 && (
              <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6, border: '0.5px solid var(--aj-border)', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {link.unexplained.rows.map(r => (
                      <tr key={r.id} style={{ borderTop: '0.5px solid var(--aj-border)' }}>
                        <td style={{ ...S.td, ...S.mono, whiteSpace: 'nowrap' }}>{r.posted_on}</td>
                        <td style={{ ...S.td, fontSize: 11 }}>{r.description}</td>
                        <td style={{ ...S.td, ...S.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Step 2 · Clear the payments still in Undeposited Funds</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={{ ...S.btn, ...S.primary }} disabled={!!busy} onClick={doPlan}>
          {busy === 'plan' ? 'Matching…' : 'Match deposits to payments'}
        </button>
        {plan && (
          <span style={{ ...S.mono, fontSize: 13 }}>
            {plan.counts.post} match ({money(plan.totals.post)}) · {plan.counts.hold} unexplained
            {plan.unmatchedPayments?.length > 0 &&
              ` · ${plan.unmatchedPayments.length} payment(s) still stranded (${money(plan.unmatchedTotal)})`}
          </span>
        )}
      </div>

      {err && <div style={{ fontSize: 13, color: '#b4493c', marginTop: 8 }}>{err}</div>}

      {/* Whether a payment has ALREADY been deposited is decided entirely by this scan. If it did
          not finish, every payment looks undeposited and recording would bank the same money
          twice with arithmetic that checks out perfectly. Say so loudly; the server refuses too. */}
      {plan?.scan && plan.scan.complete === false && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 6, fontSize: 13,
          border: '0.5px solid #b4493c', color: '#b4493c',
        }}>
          <strong>Do not record these yet.</strong> The check for payments that were already
          deposited did not finish ({plan.scan.depositsScanned} deposits seen, Core said{' '}
          {String(plan.scan.lastStatus)}). Until it completes, anything shown as undeposited might
          already be in the bank. Recording is refused server-side while this is true.
        </div>
      )}
      {plan?.scan && plan.scan.complete && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: 'DM Mono, monospace' }}>
          checked {plan.scan.depositsScanned} existing deposits · {plan.scan.clearedIds} payments
          already banked and excluded
          {(plan.scan.retainerDraws?.n > 0 || plan.scan.writeOffs?.n > 0) && (
            <>
              {' · set aside because no cash moved: '}
              {plan.scan.retainerDraws?.n > 0 && `${plan.scan.retainerDraws.n} retainer draw(s) (${money(plan.scan.retainerDraws.total)})`}
              {plan.scan.retainerDraws?.n > 0 && plan.scan.writeOffs?.n > 0 && ' · '}
              {plan.scan.writeOffs?.n > 0 && `${plan.scan.writeOffs.n} write-off(s) (${money(plan.scan.writeOffs.total)})`}
            </>
          )}
        </div>
      )}

      {plan && (
        <>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '0.5px solid var(--aj-border)', borderRadius: 6, margin: '10px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Date', 'Bank credit', 'Amount', 'Clears', 'Why'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {plan.rows.map(r => (
                  <tr key={r.id} style={{ borderTop: '0.5px solid var(--aj-border)' }}>
                    <td style={{ ...S.td, ...S.mono, whiteSpace: 'nowrap' }}>{r.posted_on}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{r.description}</td>
                    <td style={{ ...S.td, ...S.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(r.amount)}</td>
                    <td style={{ ...S.td, fontSize: 12, color: r.action === 'post' ? '#2e7d32' : '#b4493c' }}>
                      {r.action === 'post'
                        ? r.payments.map(p => (
                            <div key={p.id} style={{ ...S.mono, fontSize: 11 }}>
                              {p.date} {money(p.amount)}{p.client ? ` · ${p.client}` : ''}
                            </div>
                          ))
                        : 'nothing — held'}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: r.action === 'hold' ? '#b4493c' : 'var(--aj-text-muted)' }}>
                      {r.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...S.btn, ...(will.length ? S.primary : {}) }} disabled={!!busy || !will.length} onClick={doPost}>
              {busy === 'post' ? 'Recording…' : `Record ${will.length} deposit${will.length === 1 ? '' : 's'}`}
            </button>
            {held.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>
                {held.length} credit(s) could not be explained by any combination of undeposited
                payments — each needs a look before it can be recorded.
              </span>
            )}
          </div>

          {plan.unmatchedPayments?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--aj-text-muted)' }}>
              <strong>Still in undeposited funds after this run:</strong>{' '}
              {plan.unmatchedPayments.length} payment(s), {money(plan.unmatchedTotal)}. This is the
              number to watch reach zero.
            </div>
          )}
        </>
      )}

      {result && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <strong>{result.created.length} deposit(s) recorded.</strong>
          {result.failed?.length > 0 && (
            <div style={{ color: '#b4493c', marginTop: 6 }}>
              {result.failed.slice(0, 8).map((f, i) => (
                <div key={i} style={{ ...S.mono, fontSize: 11 }}>{String(f.id).slice(0, 8)} — {f.error}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
